import type Phaser from "phaser";

export type AstrolabeRingId = "outer" | "inner";

export type RingKinematics = {
  radius: number;
  tubeHalfThickness: number;
  cosTilt: number;
  revolutionMs: number;
  direction: 1 | -1;
  startPhase: number;
};

type RingVisualSource = {
  source: CanvasImageSource;
  width: number;
  height: number;
  majorAngle: number;
  majorDiameter: number;
  minorDiameter: number;
  centerlineRatio: number;
};

export type Astrolabe3DSources = {
  outer: RingVisualSource;
  inner: RingVisualSource;
  core: {
    source: CanvasImageSource;
    width: number;
    height: number;
    displayDiameter: number;
  };
};

export type Astrolabe3DRenderState = {
  outerPhase: number;
  innerPhase: number;
  ringScale: number;
  ringAlpha: number;
  coreScale: number;
  coreY: number;
  coreGlow: number;
};

type RingSegment = {
  source: HTMLCanvasElement;
  offsetX: number;
  offsetY: number;
  midAngle: number;
};

type ProjectedSegment = {
  source: HTMLCanvasElement;
  z: number;
  alpha: number;
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
};

const TAU = Math.PI * 2;
const LOGICAL_CANVAS_SIZE = 340;
const LOGICAL_CANONICAL_SIZE = 320;
const LOGICAL_CANONICAL_RING_RADIUS = 128;
const SEGMENT_COUNT = 24;
const SEGMENT_OVERLAP = (0.7 * Math.PI) / 180;

export const ASTROLABE_3D_CONFIG = {
  coreRadius: 80,
  outer: {
    radius: 122,
    tubeHalfThickness: 11,
    cosTilt: 0.58,
    revolutionMs: 13_500,
    direction: 1,
    startPhase: (32 * Math.PI) / 180,
  },
  inner: {
    radius: 98,
    tubeHalfThickness: 12,
    cosTilt: 0.68,
    revolutionMs: 10_500,
    direction: -1,
    startPhase: (-48 * Math.PI) / 180,
  },
} as const satisfies {
  coreRadius: number;
  outer: RingKinematics;
  inner: RingKinematics;
};

export function wrapPhase(phase: number): number {
  return ((((phase + Math.PI) % TAU) + TAU) % TAU) - Math.PI;
}

export function advanceRingPhase(phase: number, deltaMs: number, ring: RingKinematics): number {
  return wrapPhase(phase + ring.direction * TAU * (deltaMs / ring.revolutionMs));
}

export function projectRingPoint(
  ring: RingKinematics,
  phase: number,
  pointAngle: number,
): { x: number; y: number; z: number } {
  const sinTilt = Math.sqrt(1 - ring.cosTilt * ring.cosTilt);
  const cosPhase = Math.cos(phase);
  const sinPhase = Math.sin(phase);
  const cosPoint = Math.cos(pointAngle);
  const sinPoint = Math.sin(pointAngle);
  return {
    x: ring.radius * (cosPoint * cosPhase - sinPoint * ring.cosTilt * sinPhase),
    y: ring.radius * (cosPoint * sinPhase + sinPoint * ring.cosTilt * cosPhase),
    z: ring.radius * sinPoint * sinTilt,
  };
}

export function getAstrolabeClearances(): { coreToInner: number; innerToOuter: number } {
  const { coreRadius, inner, outer } = ASTROLABE_3D_CONFIG;
  return {
    coreToInner: inner.radius - inner.tubeHalfThickness - coreRadius,
    innerToOuter: outer.radius - outer.tubeHalfThickness - inner.radius - inner.tubeHalfThickness,
  };
}

export function getProjectedRingAxes(ring: RingKinematics): { major: number; minor: number } {
  return { major: ring.radius, minor: ring.radius * ring.cosTilt };
}

export class Astrolabe3DRenderer {
  readonly #texture: Phaser.Textures.CanvasTexture;
  readonly #context: CanvasRenderingContext2D;
  readonly #sources: Astrolabe3DSources;
  readonly #outerSegments: RingSegment[];
  readonly #innerSegments: RingSegment[];
  readonly #pixelRatio: number;
  readonly #canvasSize: number;
  readonly #canvasCenter: number;
  readonly #canonicalSize: number;
  readonly #canonicalCenter: number;
  readonly #canonicalRingRadius: number;

  constructor(texture: Phaser.Textures.CanvasTexture, sources: Astrolabe3DSources, pixelRatio: number) {
    this.#texture = texture;
    this.#context = texture.getContext();
    this.#sources = sources;
    this.#pixelRatio = pixelRatio;
    this.#canvasSize = LOGICAL_CANVAS_SIZE * pixelRatio;
    this.#canvasCenter = this.#canvasSize / 2;
    this.#canonicalSize = LOGICAL_CANONICAL_SIZE * pixelRatio;
    this.#canonicalCenter = this.#canonicalSize / 2;
    this.#canonicalRingRadius = LOGICAL_CANONICAL_RING_RADIUS * pixelRatio;
    this.#outerSegments = this.#createSegments(this.#createCanonicalRing(sources.outer));
    this.#innerSegments = this.#createSegments(this.#createCanonicalRing(sources.inner));
  }

  render(state: Astrolabe3DRenderState): void {
    const context = this.#context;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, this.#canvasSize, this.#canvasSize);
    context.imageSmoothingEnabled = true;

    const projected = [
      ...this.#projectSegments(this.#outerSegments, ASTROLABE_3D_CONFIG.outer, state.outerPhase, state),
      ...this.#projectSegments(this.#innerSegments, ASTROLABE_3D_CONFIG.inner, state.innerPhase, state),
    ].sort((left, right) => left.z - right.z);

    let coreDrawn = false;
    for (const segment of projected) {
      if (!coreDrawn && segment.z >= 0) {
        this.#drawCore(state);
        coreDrawn = true;
      }
      this.#drawSegment(segment);
    }
    if (!coreDrawn) this.#drawCore(state);
    this.#texture.refresh();
  }

  #createCanonicalRing(visual: RingVisualSource): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = this.#canonicalSize;
    canvas.height = this.#canonicalSize;
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("Astrolabe canonical canvas is unavailable");
    context.imageSmoothingEnabled = true;
    context.translate(this.#canonicalCenter, this.#canonicalCenter);
    context.scale(
      (this.#canonicalRingRadius * 2) / visual.majorDiameter,
      (this.#canonicalRingRadius * 2) / visual.minorDiameter,
    );
    context.rotate(-visual.majorAngle);
    context.translate(-visual.width / 2, -visual.height / 2);
    context.drawImage(visual.source, 0, 0, visual.width, visual.height);
    return canvas;
  }

  #createSegments(canonical: HTMLCanvasElement): RingSegment[] {
    const segments: RingSegment[] = [];
    const segmentAngle = TAU / SEGMENT_COUNT;
    for (let index = 0; index < SEGMENT_COUNT; index += 1) {
      const start = index * segmentAngle - SEGMENT_OVERLAP;
      const end = (index + 1) * segmentAngle + SEGMENT_OVERLAP;
      const bounds = this.#segmentBounds(start, end);
      const canvas = document.createElement("canvas");
      canvas.width = bounds.width;
      canvas.height = bounds.height;
      const context = canvas.getContext("2d");
      if (context === null) throw new Error("Astrolabe segment canvas is unavailable");
      context.imageSmoothingEnabled = true;
      context.translate(-bounds.x, -bounds.y);
      context.beginPath();
      context.moveTo(this.#canonicalCenter, this.#canonicalCenter);
      context.arc(this.#canonicalCenter, this.#canonicalCenter, this.#canonicalSize, start, end);
      context.closePath();
      context.clip();
      context.drawImage(canonical, 0, 0);
      segments.push({
        source: canvas,
        offsetX: bounds.x,
        offsetY: bounds.y,
        midAngle: (index + 0.5) * segmentAngle,
      });
    }
    return segments;
  }

  #segmentBounds(start: number, end: number): { x: number; y: number; width: number; height: number } {
    const points: { x: number; y: number }[] = [];
    for (let sample = 0; sample <= 8; sample += 1) {
      const angle = start + (end - start) * (sample / 8);
      for (const radius of [52 * this.#pixelRatio, 136 * this.#pixelRatio]) {
        points.push({
          x: this.#canonicalCenter + Math.cos(angle) * radius,
          y: this.#canonicalCenter + Math.sin(angle) * radius,
        });
      }
    }
    const padding = 3 * this.#pixelRatio;
    const minX = Math.max(0, Math.floor(Math.min(...points.map((point) => point.x)) - padding));
    const minY = Math.max(0, Math.floor(Math.min(...points.map((point) => point.y)) - padding));
    const maxX = Math.min(this.#canonicalSize, Math.ceil(Math.max(...points.map((point) => point.x)) + padding));
    const maxY = Math.min(this.#canonicalSize, Math.ceil(Math.max(...points.map((point) => point.y)) + padding));
    return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
  }

  #projectSegments(
    segments: RingSegment[],
    ring: RingKinematics,
    phase: number,
    state: Astrolabe3DRenderState,
  ): ProjectedSegment[] {
    const sinTilt = Math.sqrt(1 - ring.cosTilt * ring.cosTilt);
    const cosPhase = Math.cos(phase);
    const sinPhase = Math.sin(phase);
    const visual = ring === ASTROLABE_3D_CONFIG.outer ? this.#sources.outer : this.#sources.inner;
    const canonicalCenterline = this.#canonicalRingRadius * visual.centerlineRatio;
    const scale = ((ring.radius * this.#pixelRatio) / canonicalCenterline) * state.ringScale;
    const a = scale * cosPhase;
    const b = scale * sinPhase;
    const c = scale * -ring.cosTilt * sinPhase;
    const d = scale * ring.cosTilt * cosPhase;
    const baseE = this.#canvasCenter - a * this.#canonicalCenter - c * this.#canonicalCenter;
    const baseF = this.#canvasCenter - b * this.#canonicalCenter - d * this.#canonicalCenter;
    return segments.map((segment) => {
      const depth = Math.sin(segment.midAngle) * sinTilt;
      return {
        source: segment.source,
        z: ring.radius * depth,
        alpha: state.ringAlpha * (0.86 + (depth + 1) * 0.07),
        a,
        b,
        c,
        d,
        e: baseE + a * segment.offsetX + c * segment.offsetY,
        f: baseF + b * segment.offsetX + d * segment.offsetY,
      };
    });
  }

  #drawSegment(segment: ProjectedSegment): void {
    const context = this.#context;
    context.save();
    context.globalAlpha = segment.alpha;
    context.globalCompositeOperation = "source-over";
    context.setTransform(segment.a, segment.b, segment.c, segment.d, segment.e, segment.f);
    context.drawImage(segment.source, 0, 0);
    context.restore();
  }

  #drawCore(state: Astrolabe3DRenderState): void {
    const context = this.#context;
    const { core } = this.#sources;
    const diameter = core.displayDiameter * this.#pixelRatio * state.coreScale;
    const left = this.#canvasCenter - diameter / 2;
    const top = this.#canvasCenter - diameter / 2 + state.coreY * this.#pixelRatio;
    context.save();
    context.globalAlpha = 1;
    context.globalCompositeOperation = "source-over";
    context.drawImage(core.source, left, top, diameter, diameter);
    if (state.coreGlow > 0) {
      context.globalAlpha = state.coreGlow;
      context.globalCompositeOperation = "screen";
      context.drawImage(core.source, left, top, diameter, diameter);
    }
    context.restore();
  }
}

export const ASTROLABE_3D_LOGICAL_SIZE = LOGICAL_CANVAS_SIZE;
